// libs
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import escape from 'lodash/escape';

// api
import { DEVICE_TYPE, getProductOffers } from 'shared/api/product-offers';

// context
import { useDomainConfig } from 'shared/domainConfig/context/context';

// components
import { Coupon, COUPON_TYPE } from 'shared/layouts/Coupons/types';

import Placeholder from './components/PlaceHolder';
import CouponOfferDetails from './couponOfferDetails';

// constants
import {
  BUTTON_TYPE,
  COUPON_CODE_COPIED_TEXT,
  TEXTS,
  COUPON_DELTA_EXPERIMENT,
  COUPON_DELTA_EXPERIMENT_EXP,
} from 'shared/layouts/Coupons/constants';

//constants
import { COUPON_EXPIRY_REMOTE_CONFIG } from 'shared/layouts/Coupons/constants';
import { OFFER_PROPERTIES, DefaultApiData, API_STATUS } from './constants';

// styles
import {
  ActionContainer,
  Button,
  CardsContainer,
  Container,
  ContainerWrapper,
  Content,
  CouponCode,
  CouponCodeContainer,
  CouponsContainer,
  CustomToast,
  Dot,
  HeaderTitle,
  OffersCountText,
  TitleContainer,
} from './styles';
import { CouponRowPDP } from 'shared/layouts/Coupons/CouponsRenderer/styles';
import { BlockSeparator } from 'shared/layouts/DetailsPage/components/ProductInfo/Mobile/styles';

// hook
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { getEligibleVariantName } from 'shared/components/ExperimentV2/helpers';
import useCouponWidgetMode, { COUPON_WIDGET_MODE } from 'shared/hooks/useCouponWidgetMode';

// Analytics
import { setCouponOfferData } from './analytics';
import {
  onCouponSeeDetailsClicked,
  onOfferSeeDetailsClicked,
  onCouponCodeCopyClicked,
  onCouponsDetailsLoaded,
} from 'shared/layouts/Coupons/analytics';
import NFA from 'shared/helpers/analytics/NFAnalytics';

//helpers
import { getValidCouponsData } from 'shared/helpers/api/helpers';

function ProductOffers({
  parentProductId,
  productId,
  isFetchingDetails,
  isFetchingError,
  isUserFetching,
  isPostRequestAllowed,
  isNotFound,
  isLoggedIn,
  isDesktop = false,
  setProductOffers,
  setOffersFailure,
  mrp,
  sp,
  cartCount,
  isInStock,
  vendorId,
  vendorType,
}: // eslint-disable-next-line  no-undef
  ProductOffers.IProductOffers) {
  const [apiStatus, setApiStatus] = useState<API_STATUS>(API_STATUS.IDLE);
  // eslint-disable-next-line  no-undef
  const [apiData, setApiData] = useState<ProductOffers.IOfferApiOut['data']>([]);

  const {
    isEligible: isEligibleForDeltaCommunicationVariant = false,
    variant: userDeltaCommunicationVariant,
  } = getEligibleVariantName(COUPON_DELTA_EXPERIMENT);
  const isEligibleForDeltaCommunication =
    isEligibleForDeltaCommunicationVariant &&
    userDeltaCommunicationVariant == COUPON_DELTA_EXPERIMENT_EXP.COUPON_DELTA_EXPERIMENT_EXP_A;

  const [showDetailCoupon, setshowDetailCoupon] = useState<{
    showDetails: boolean;
    coupon: Coupon;
    disabled: boolean;
    type: string;
  }>({});
  const [copySuccessMessage, setCopySuccessMessage] = useState<string>('');
  const { id: unsafeRouteId } = useParams<{ id: string; }>();
  const { enabled = false } = useRemoteConfig('pdpOffersAndCoupns');
  const routeId = escape(unsafeRouteId);
  const { enabled: expiryEnabled, location: couponExpiryLocation } = useRemoteConfig(
    COUPON_EXPIRY_REMOTE_CONFIG
  );
  const isBestPriceEnabled = useRemoteConfig('bestPriceEnabled', false);
  const couponWidgetMode = useCouponWidgetMode();
  const isMulticoupon = couponWidgetMode === COUPON_WIDGET_MODE.MULTICOUPON;
  const { LOGO: { COUPONS = {}, OFFERS = {} } = {}, COUPONS_IMAGES } = useDomainConfig();

  const couponBackgroundImage = COUPONS.BACKGROUNDURL
    ? COUPONS.BACKGROUNDURL
    : COUPONS_IMAGES.COUPON_BACKGROUND_IMAGE;

  const offersBackgroundImage = OFFERS.BACKGROUNDURL
    ? OFFERS.BACKGROUNDURL
    : COUPONS_IMAGES.COUPON_BACKGROUND_IMAGE;

  const fetchOffersV2 = useCallback(() => {
    setApiData(DefaultApiData);

    // do not fetch if productid is invalid
    if (routeId !== parentProductId) {
      return;
    }

    if (isPostRequestAllowed) {
      setProductOffers({ ...DefaultApiData, productId, fetching: true });
      return getProductOffers({
        productId: String(productId),
        isLoggedIn,
        deviceType: isDesktop ? DEVICE_TYPE.DESKTOP : DEVICE_TYPE.MOBILE,
        mrp,
        sp,
        isBestPriceEnabled,
        vendorId,
        vendorType,
        isMulticoupon,
      }) // eslint-disable-next-line  no-undef
        .then((response: ProductOffers.IOfferApiOut) => {
          setApiData(
            {
              ...response?.data,
              productId,
              coupons: getValidCouponsData(response?.data?.coupons),
              offers: getValidCouponsData(response?.data?.offers),
            } || DefaultApiData
          );
          setApiStatus(API_STATUS.SUCCESS);
          setCouponOfferData(
            response?.data?.offers?.length,
            response?.data?.coupons?.length,
            parentProductId
          );
          setProductOffers({ ...response?.data, productId, fetching: false });
          NFA.updateDataLayer({
            isOffersAvailable: response?.data?.offers?.length > 0,
            isCouponsAvailable: response?.data?.coupons?.length > 0,
          });
          onCouponsDetailsLoaded(
            response?.data?.offers,
            response?.data?.coupons,
            isEligibleForDeltaCommunication
          );
        })
        .catch(() => {
          setOffersFailure();
          setProductOffers({ ...DefaultApiData, productId, fetching: false });
          setApiStatus(API_STATUS.FAILURE);
        });
    }

    return;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    routeId,
    parentProductId,
    isPostRequestAllowed,
    setProductOffers,
    productId,
    isLoggedIn,
    isDesktop,
    mrp,
    sp,
    isBestPriceEnabled,
    vendorId,
    vendorType,
    setOffersFailure,
    cartCount,
  ]);

  useEffect(() => {
    setApiStatus(API_STATUS.IDLE);

    if (productId && isPostRequestAllowed) fetchOffersV2();
    return () => {
      setProductOffers({ ...DefaultApiData, fetching: false });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, isPostRequestAllowed, cartCount, vendorId, vendorType]);

  const onCopyClicked = (code: string, coupon: Coupon, isDetailsBottomSheet?: boolean) => {
    if (!navigator.clipboard) {
      return;
    }

    navigator.clipboard.writeText(code).then(
      function () {
        setCopySuccessMessage(COUPON_CODE_COPIED_TEXT.SUCCESS);
      },
      function () {
        setCopySuccessMessage(COUPON_CODE_COPIED_TEXT.FAILED);
      }
    );
    onCouponCodeCopyClicked(coupon, isEligibleForDeltaCommunication, isDetailsBottomSheet);
  };

  const onCopySuccessToastHide = () => {
    setCopySuccessMessage('');
  };

  const couponActionContainer = (
    coupon: Coupon,
    onClickCopy: (couponCode: string, coupon: Coupon) => void,
    type?: COUPON_TYPE
  ) => {
    if (type === COUPON_TYPE.COUPON) {
      const { couponDetails } = coupon;
      const couponCode = couponDetails?.couponCode ?? (coupon as any).couponCode ?? '';
      return (
        <ActionContainer className="action-container">
          <CouponCodeContainer>
            <CouponCode className="couponCode">{couponCode}</CouponCode>
          </CouponCodeContainer>
          <Button
            disabled={false}
            isApplied={false}
            onClick={() => onClickCopy(couponCode, coupon)}
          >
            {TEXTS.COPY_CODE}
          </Button>
        </ActionContainer>
      );
    } else return <></>;
  };
  const couponsLength = apiData?.coupons?.length;
  const offersLength = apiData?.offers?.length;

  const setShowDetailCouponData = (
    couponDetail: Coupon,
    disabled: boolean,
    couponType: COUPON_TYPE
  ) => {
    if (couponType === COUPON_TYPE.COUPON) {
      setshowDetailCoupon({
        showDetails: true,
        coupon: couponDetail,
        disabled,
        type: COUPON_TYPE.COUPON,
      });
      // analytics
      onCouponSeeDetailsClicked(couponDetail, isEligibleForDeltaCommunication);
      // onPDPCouponViewDetailsClicked(couponDetail?.couponDetails?.couponCode);
    }
    if (couponType === COUPON_TYPE.OFFER) {
      setshowDetailCoupon({
        showDetails: true,
        coupon: couponDetail,
        disabled,
        type: COUPON_TYPE.OFFER,
      });
      // analytics
      onOfferSeeDetailsClicked(couponDetail, isEligibleForDeltaCommunication);
      // onPDPOfferViewDetailsClicked(couponDetail?.couponDetails?.couponCode);
    }
  };

  if (enabled && isUserFetching) return <Placeholder isDesktop={isDesktop} />;

  if (
    !enabled ||
    isUserFetching ||
    isFetchingDetails ||
    !isPostRequestAllowed ||
    isFetchingError ||
    isNotFound ||
    apiStatus === API_STATUS.FAILURE ||
    (apiStatus !== API_STATUS.IDLE && offersLength <= 0 && couponsLength <= 0)
  )
    return null;

  let couponClass = 'couponContainer';
  let offerClass = 'couponContainer';

  if (couponsLength === 1 && isDesktop === false) {
    couponClass = 'couponContainerFull';
  }

  if (offersLength === 1 && isDesktop === false) {
    offerClass = 'couponContainerFull';
  }

  if (apiStatus === API_STATUS.SUCCESS && (offersLength > 0 || couponsLength > 0)) {
    return (
      <>
        <ContainerWrapper data-test="container" parentOverflow={!isDesktop}>
          <Container>
            {couponsLength > 0 && (
              <>
                <TitleContainer havePaddingLeft={false}>
                  <HeaderTitle data-test="coupon-title">
                    {OFFER_PROPERTIES.COUPON_TITLE}
                  </HeaderTitle>
                  <Dot />
                  <OffersCountText>
                    {couponsLength} {OFFER_PROPERTIES.AVAILABLE_TEXT}
                  </OffersCountText>
                </TitleContainer>
                <Content hasExtraMargin offersAvailable={offersLength > 0 || false}>
                  <CardsContainer>
                    <CouponsContainer hideScroll={!isDesktop}>
                      {apiData?.coupons?.map((coupon, id) => (
                        <CouponRowPDP
                          key={`coupon-row-pdp-${coupon.couponDetails?.couponCode || id}`}
                          coupon={{ ...coupon, subTitle: coupon?.description }}
                          disabled={false}
                          isApplied={false}
                          backgroundImage={couponBackgroundImage}
                          onDetailsClicked={(couponDetail, disabled) =>
                            setShowDetailCouponData(couponDetail, disabled, COUPON_TYPE.COUPON)
                          }
                          onApplyClicked={() => { }}
                          onRemoveClicked={() => { }}
                          onCopyClicked={onCopyClicked}
                          actionContainer={() =>
                            couponActionContainer(coupon, onCopyClicked, COUPON_TYPE.COUPON)
                          }
                          actionButtonType={BUTTON_TYPE.COPY}
                          hasScrollSnap={couponsLength > 1}
                          type={COUPON_TYPE.COUPON}
                          className={couponClass}
                          logoHeight={OFFER_PROPERTIES.LOGO_HEIGHT}
                          logoWidth={OFFER_PROPERTIES.LOGO_WIDTH}
                          subtitleTrimLength={
                            isDesktop
                              ? OFFER_PROPERTIES.DESCRIPTION_LENGTH_DESKTOP
                              : OFFER_PROPERTIES.DESCRIPTION_LENGTH_MOBILE
                          }
                          couponExpiryEnabled={expiryEnabled && couponExpiryLocation?.pdp}
                          isEligibleForDeltaCommunication={isEligibleForDeltaCommunication}
                          isPDP={true}
                          isInStock={isInStock}
                        />
                      ))}
                    </CouponsContainer>
                  </CardsContainer>
                </Content>
              </>
            )}
            {offersLength > 0 && (
              <>
                <TitleContainer havePaddingLeft={false}>
                  <HeaderTitle data-test="offer-title">{OFFER_PROPERTIES.OFFER_TITLE}</HeaderTitle>
                  <Dot />

                  <OffersCountText>
                    {offersLength} {OFFER_PROPERTIES.AVAILABLE_TEXT}
                  </OffersCountText>
                </TitleContainer>
                <Content>
                  <CardsContainer>
                    <CouponsContainer hideScroll={!isDesktop}>
                      {apiData?.offers?.map((coupon, id) => (
                        <CouponRowPDP
                          key={`offer-row-pdp-${coupon.couponDetails?.couponCode || id}`}
                          coupon={{ ...coupon, subTitle: coupon?.description }}
                          disabled={false}
                          isApplied={false}
                          backgroundImage={offersBackgroundImage}
                          onDetailsClicked={(couponDetail, disabled) =>
                            setShowDetailCouponData(couponDetail, disabled, COUPON_TYPE.OFFER)
                          }
                          onApplyClicked={() => { }}
                          onRemoveClicked={() => { }}
                          onCopyClicked={onCopyClicked}
                          actionContainer={() =>
                            couponActionContainer(coupon, onCopyClicked, COUPON_TYPE.OFFER)
                          }
                          actionButtonType={BUTTON_TYPE.COPY}
                          hasScrollSnap={offersLength > 1}
                          type={COUPON_TYPE.OFFER}
                          className={offerClass}
                          logoHeight={OFFER_PROPERTIES.LOGO_HEIGHT}
                          logoWidth={OFFER_PROPERTIES.LOGO_WIDTH}
                          subtitleTrimLength={
                            isDesktop
                              ? OFFER_PROPERTIES.DESCRIPTION_LENGTH_DESKTOP
                              : OFFER_PROPERTIES.DESCRIPTION_LENGTH_MOBILE
                          }
                          extraInfoPadding
                          couponExpiryEnabled={expiryEnabled && couponExpiryLocation?.pdp}
                          isOffer={true}
                          isEligibleForDeltaCommunication={isEligibleForDeltaCommunication}
                          isPDP={true}
                          isInStock={isInStock}
                        />
                      ))}
                    </CouponsContainer>
                  </CardsContainer>
                </Content>
              </>
            )}
          </Container>
          {showDetailCoupon.showDetails && (
            <CouponOfferDetails
              setCopySuccessMessage={setCopySuccessMessage}
              showDetailCoupon={showDetailCoupon}
              setshowDetailCoupon={setshowDetailCoupon}
              isDesktop={isDesktop}
              onCopyClicked={onCopyClicked}
              isEligibleForDeltaCommunication={isEligibleForDeltaCommunication}
            />
          )}
          {copySuccessMessage && copySuccessMessage.length > 0 && (
            <CustomToast
              className="pdpToast"
              message={copySuccessMessage}
              onToastHide={onCopySuccessToastHide}
              showTextCenter
            />
          )}
        </ContainerWrapper>
        {!isDesktop && <BlockSeparator marginTop="0" />}
      </>
    );
  }

  return <Placeholder isDesktop={isDesktop} />;
}

export default memo(ProductOffers);
